
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CornerDownLeft, Folder, Pencil, Plus, X } from 'lucide-react';
import { Field, Input } from '@/shared/ui';
import { userFieldValues } from '@/shared/api/forms';
import { UserField } from './UserField';
import { cachedFetch } from '@/shared/lib/refCache';
import { cn } from '@/shared/lib/cn';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { foldersApi, type FolderMatch, type FolderPick } from '@/shared/api';
import {
  FOLDER_ACCESS_LABELS,
  FOLDER_ACCESS_TYPES,
  MAX_FOLDER_SUBJECTS,
  folderValues,
  type FolderAccessType,
  type FolderSubject,
  type FolderValue,
  type FormField,
} from '@/shared/api/forms';
import styles from './FolderField.module.css';

const CACHE_TTL_MS = 60_000;
const MAX_SUGGESTIONS = 8;

interface SuggestionRow {
  key: string;
  node: FolderPick | null;
  path: string;
  name: string;
  subtitle: string;
  leaf: boolean;
}

function normalizeTyped(raw: string): string {
  const cleaned = raw.replace(/\\/g, '/').replace(/\/{2,}/g, '/');
  return cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
}

function toPath(raw: string): string {
  const path = normalizeTyped(raw.trim()).replace(/\/+$/, '');
  return path === '/' ? '' : path;
}

const activeSuggestion = `${styles.suggestion} ${styles.suggestionActive}`;

const ACCESS_SHORT: Record<FolderAccessType, string> = {
  read: 'Чтение',
  write: 'Запись',
  full: 'Полный',
};

export interface FolderFieldProps {
  field: FormField;
  value: unknown;
  disabled?: boolean;
  error?: React.ReactNode;
  onChange: (value: unknown) => void;
}

export const FolderField: React.FC<FolderFieldProps> = ({
  field,
  value,
  disabled,
  error,
  onChange,
}) => {
  const [nodes, setNodes] = useState<FolderPick[]>([]);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [matches, setMatches] = useState<FolderMatch[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);

  const picked = useMemo(() => folderValues(value), [value]);
  const multiple = field.display?.multiple === true;

  const [typed, setTyped] = useState(() => (multiple ? '' : (picked[0]?.path ?? '')));
  const [draftAccess, setDraftAccess] = useState<FolderAccessType>(
    () => (multiple ? 'read' : (picked[0]?.accessType ?? 'read')),
  );
  const [draftSubjects, setDraftSubjects] = useState<FolderSubject[]>(
    () => (multiple ? [] : (picked[0]?.subjects ?? [])),
  );

  useEffect(() => {
    cachedFetch('folders:tree', CACHE_TTL_MS, () => foldersApi.tree().then((res) => res.data))
      .then(setNodes)
      .catch(() => setFailed(true));
  }, []);

  const singlePath = multiple ? '' : (picked[0]?.path ?? '');
  useEffect(() => {
    if (multiple) return;
    setTyped((prev) => (toPath(prev) === singlePath ? prev : singlePath));
  }, [multiple, singlePath]);

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  const emit = useCallback(
    (items: FolderValue[]) => {
      if (multiple) onChange(items);
      else onChange(items[0]);
    },
    [multiple, onChange],
  );

  const valueOf = useCallback(
    (path: string, accessType: FolderAccessType, subjects: FolderSubject[]): FolderValue => {
      const node = nodes.find((item) => item.path === path);
      return {
        ...(node ? { nodeId: node.id } : {}),
        path,
        accessType,
        ...(subjects.length > 0 ? { subjects } : {}),
      };
    },
    [nodes],
  );

  const setTypedValue = (raw: string) => {
    setTyped(raw);
    if (multiple) return;
    const path = toPath(raw);
    emit(path === '' ? [] : [valueOf(path, draftAccess, draftSubjects)]);
  };

  const syncSingle = (accessType: FolderAccessType, subjects: FolderSubject[]) => {
    if (multiple) return;
    const path = toPath(typed);
    emit(path === '' ? [] : [valueOf(path, accessType, subjects)]);
  };

  const changeAccess = (accessType: FolderAccessType) => {
    setDraftAccess(accessType);
    syncSingle(accessType, draftSubjects);
  };

  const changeSubjects = (value: unknown) => {
    const next: FolderSubject[] = userFieldValues(value)
      .slice(0, MAX_FOLDER_SUBJECTS)
      .map((v) => (v.id ? { id: v.id, label: v.label } : { label: v.label, external: true }));
    setDraftSubjects(next);
    syncSingle(draftAccess, next);
  };

  const askedPaths = useMemo(() => {
    const list = picked.map((item) => item.path);
    const current = toPath(typed);
    if (current !== '' && !list.includes(current)) list.push(current);
    return list;
  }, [picked, typed]);
  const askedKey = useDebouncedValue(askedPaths.join('|'), 350);

  useEffect(() => {
    if (askedKey === '') {
      setMatches([]);
      return;
    }
    let cancelled = false;
    foldersApi
      .resolve(askedKey.split('|'))
      .then((res) => !cancelled && setMatches(res.data))
      .catch(() => !cancelled && setMatches([]));
    return () => {
      cancelled = true;
    };
  }, [askedKey]);

  const matchOf = (path: string) => matches.find((item) => item.path === path) ?? null;
  const lockedCommittee = useMemo(
    () => picked.map((item) => matchOf(item.path)).find((item) => item?.committeeId) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [picked, matches],
  );

  const suggestions = useMemo(() => {
    const pickedPaths = new Set(picked.map((item) => item.path));
    const allowed = nodes.filter((node) => {
      if (pickedPaths.has(node.path)) return false;
      if (!multiple || !lockedCommittee?.committeeId) return true;
      return !node.committeeId || node.committeeId === lockedCommittee.committeeId;
    });

    const raw = typed.trim();
    if (raw === '' || raw === '/') {
      return allowed.filter((node) => node.depth === 1).slice(0, MAX_SUGGESTIONS);
    }

    const path = normalizeTyped(raw);
    if (path.endsWith('/')) {
      const parent = path.slice(0, -1);
      return allowed
        .filter(
          (node) =>
            node.path.startsWith(`${parent}/`) &&
            node.path.indexOf('/', parent.length + 1) === -1,
        )
        .slice(0, MAX_SUGGESTIONS);
    }

    const needle = path.toLowerCase();
    const tail = needle.split('/').pop() ?? '';
    return allowed
      .filter(
        (node) =>
          node.path.toLowerCase().startsWith(needle) || node.segment.toLowerCase().includes(tail),
      )
      .sort((left, right) => {
        const l = left.path.toLowerCase().startsWith(needle) ? 0 : 1;
        const r = right.path.toLowerCase().startsWith(needle) ? 0 : 1;
        return l - r || left.path.length - right.path.length;
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [nodes, typed, picked, multiple, lockedCommittee]);

  const rows = useMemo<SuggestionRow[]>(
    () =>
      suggestions.map((node) => ({
        key: node.id,
        node,
        path: node.path,
        name: node.segment,
        subtitle: node.path,
        leaf: !nodes.some((item) => item.parentId === node.id),
      })),
    [suggestions, nodes],
  );

  useEffect(() => setHighlight(0), [typed]);

  const applySuggestion = (row: SuggestionRow) => {
    setTypedValue(row.leaf ? row.path : `${row.path}/`);
    setOpen(!row.leaf);
  };

  const addTyped = () => {
    const path = toPath(typed);
    if (path === '' || picked.some((item) => item.path === path)) return;
    emit([...picked, valueOf(path, draftAccess, draftSubjects)]);
    setTyped('');
    setOpen(false);
  };

  const editPicked = (item: FolderValue) => {
    emit(picked.filter((other) => other.path !== item.path));
    setTyped(item.path);
    setDraftAccess(item.accessType ?? 'read');
    setDraftSubjects(item.subjects ?? []);
    setOpen(true);
  };

  const removePicked = (path: string) => emit(picked.filter((item) => item.path !== path));

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setHighlight((prev) => {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const count = rows.length || 1;
        return (prev + step + count) % count;
      });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const row = open ? rows[highlight] : undefined;
      if (row) {
        applySuggestion(row);
        return;
      }
      if (multiple) addTyped();
      else setOpen(false);
      return;
    }
    if (event.key === 'Escape') setOpen(false);
  };

  if (failed) {
    return <p className={styles.notice}>Не удалось загрузить список папок. Обновите страницу.</p>;
  }

  const currentPath = toPath(typed);
  const currentMatch = currentPath === '' ? null : matchOf(currentPath);

  return (
    <div className={styles.folder} ref={boxRef}>
      <div className={styles.inputRow}>
        <Field label={field.label} required={field.required} hint={field.hint} error={error}>
          <Input
            value={typed}
            onChange={(e) => setTypedValue(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={field.placeholder ?? '/company/server-pool/…'}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
            aria-expanded={open}
          />
        </Field>

        {open && rows.length > 0 && (
          <ul className={styles.suggestions} role="listbox">
            {rows.map((row, index) => (
              <li key={row.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === highlight}
                  className={index === highlight ? activeSuggestion : styles.suggestion}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => applySuggestion(row)}
                >
                  <span className={styles.suggestionIcon} aria-hidden>
                    <Folder size={15} />
                  </span>
                  <span className={styles.suggestionText}>
                    <span className={styles.suggestionName}>{row.name}</span>
                    <span className={styles.suggestionPath}>{row.subtitle}</span>
                  </span>
                  {row.leaf ? (
                    <CornerDownLeft size={13} className={styles.suggestionHint} aria-hidden />
                  ) : (
                    <span className={styles.suggestionHint}>внутрь</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      { }
      {currentPath !== '' && (
        <div className={styles.setup}>
          <div className={styles.setupRow}>
            <span className={styles.setupLabel}>Доступ</span>
            { }
            <div className={styles.segments} role="radiogroup" aria-label="Тип доступа">
              {FOLDER_ACCESS_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={draftAccess === type}
                  title={FOLDER_ACCESS_LABELS[type]}
                  className={cn(styles.segment, draftAccess === type && styles.segmentActive)}
                  onClick={() => changeAccess(type)}
                  disabled={disabled}
                >
                  {ACCESS_SHORT[type]}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.setupRow}>
            <span className={styles.setupLabel}>Кому</span>
            <div className={styles.setupControl}>
              <UserField
                value={draftSubjects}
                multiple
                allowOther
                placeholder="Вам — или найдите сотрудника"
                disabled={disabled}
                onChange={changeSubjects}
              />
            </div>
          </div>

          <div className={styles.setupFoot}>
            <span className={styles.currentHint}>
              {currentMatch?.committeeName
                ? `Согласует «${currentMatch.committeeName}»`
                : 'Согласование не потребуется'}
              {currentMatch && !currentMatch.known && ' · папки нет в списке — заведём при выдаче'}
              {currentMatch?.alreadyGranted && ' · доступ уже выдавался'}
            </span>
            {multiple && (
              <button
                type="button"
                className={styles.addBtn}
                onClick={addTyped}
                disabled={disabled}
              >
                <Plus size={14} aria-hidden />
                Добавить папку
              </button>
            )}
          </div>
        </div>
      )}

      { }
      {picked.length > 0 && multiple && (
        <ul className={styles.picked}>
          {picked.map((item) => {
            const match = matchOf(item.path);
            return (
              <li key={item.path} className={styles.pickedItem}>
                <span className={styles.pickedIcon} aria-hidden>
                  <Folder size={15} />
                </span>
                <span className={styles.pickedPath}>{item.path}</span>
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => editPicked(item)}
                  disabled={disabled}
                  aria-label={`Изменить ${item.path}`}
                >
                  <Pencil size={13} aria-hidden />
                </button>
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => removePicked(item.path)}
                  disabled={disabled}
                  aria-label={`Убрать ${item.path}`}
                >
                  <X size={14} aria-hidden />
                </button>
                <span className={styles.pickedHint}>
                  {FOLDER_ACCESS_LABELS[item.accessType ?? 'read']}
                  {' · '}
                  {item.subjects?.length
                    ? item.subjects.map((subject) => subject.label).join(', ')
                    : 'мне'}
                  {match?.committeeName && ` · согласует «${match.committeeName}»`}
                  {match?.alreadyGranted && ' · доступ уже выдавался'}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {multiple && lockedCommittee?.committeeName && (
        <p className={styles.notice}>
          Показаны папки комитета «{lockedCommittee.committeeName}»: папку другого комитета нужно
          запросить отдельной заявкой.
        </p>
      )}
    </div>
  );
};
